// src/pages/Settings.js
import React, { useState, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLang } from '../context/LanguageContext';
import { updateProfile, updatePassword, EmailAuthProvider, reauthenticateWithCredential } from 'firebase/auth';
import { auth } from '../firebase/config';

const LOGO_KEY = 'coalab_user_logo';
export const getUserLogo = () => localStorage.getItem(LOGO_KEY);
export const clearUserLogo = () => localStorage.removeItem(LOGO_KEY);

const TABS = ['profile', 'company', 'preferences'];

export default function Settings() {
  const { user } = useAuth();
  const { lang, switchLang } = useLang();
  const [activeTab, setActiveTab] = useState('profile');

  const tx = {
    en: {
      title: 'Settings',
      tabs: { profile: '👤 Profile', company: '🏢 Company & Logo', preferences: '⚙️ Preferences' },
    },
    de: {
      title: 'Einstellungen',
      tabs: { profile: '👤 Profil', company: '🏢 Unternehmen & Logo', preferences: '⚙️ Einstellungen' },
    },
  };
  const t = tx[lang] || tx.en;

  return (
    <div style={s.page}>
      <h1 style={s.pageTitle}>{t.title}</h1>

      {/* Tab bar */}
      <div style={s.tabBar}>
        {TABS.map(tab => (
          <button
            key={tab}
            style={{ ...s.tab, ...(activeTab === tab ? s.tabActive : {}) }}
            onClick={() => setActiveTab(tab)}
          >
            {t.tabs[tab]}
          </button>
        ))}
      </div>

      {/* Tab panels */}
      <div style={s.panel}>
        {activeTab === 'profile'     && <ProfileTab     user={user} lang={lang} />}
        {activeTab === 'company'     && <CompanyTab     lang={lang} />}
        {activeTab === 'preferences' && <PreferencesTab lang={lang} switchLang={switchLang} />}
      </div>
    </div>
  );
}

// ── Tab 1: Profile ───────────────────────────────────────────────────────────
function ProfileTab({ user, lang }) {
  const [name,        setName]        = useState(user?.displayName || '');
  const [currentPass, setCurrentPass] = useState('');
  const [newPass,     setNewPass]     = useState('');
  const [confirmPass, setConfirmPass] = useState('');
  const [toast,       setToast]       = useState({ msg: '', type: '' });
  const [loading,     setLoading]     = useState(false);

  const tx = {
    en: {
      profileSection: 'Personal Information',
      nameLabel: 'Display Name', emailLabel: 'Email address',
      emailNote: 'Email cannot be changed after registration.',
      saveProfile: 'Save Changes', profileSaved: 'Profile updated!',
      passwordSection: 'Change Password',
      currentPass: 'Current password', newPass: 'New password',
      confirmPass: 'Confirm new password',
      savePassword: 'Update Password', passSaved: 'Password updated!',
      passNoMatch: 'Passwords do not match.',
      passShort: 'Password must be at least 6 characters.',
      reAuthError: 'Current password is incorrect.',
      avatar: 'Your Avatar',
      avatarNote: 'Auto-generated from your name initials.',
    },
    de: {
      profileSection: 'Persönliche Daten',
      nameLabel: 'Anzeigename', emailLabel: 'E-Mail-Adresse',
      emailNote: 'E-Mail kann nach der Registrierung nicht geändert werden.',
      saveProfile: 'Änderungen speichern', profileSaved: 'Profil aktualisiert!',
      passwordSection: 'Passwort ändern',
      currentPass: 'Aktuelles Passwort', newPass: 'Neues Passwort',
      confirmPass: 'Passwort bestätigen',
      savePassword: 'Passwort aktualisieren', passSaved: 'Passwort aktualisiert!',
      passNoMatch: 'Passwörter stimmen nicht überein.',
      passShort: 'Passwort muss mindestens 6 Zeichen haben.',
      reAuthError: 'Aktuelles Passwort ist falsch.',
      avatar: 'Ihr Avatar',
      avatarNote: 'Automatisch aus Initialen generiert.',
    },
  };
  const t = tx[lang] || tx.en;

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast({ msg: '', type: '' }), 3500);
  };

  const initials = (user?.displayName || user?.email || 'U')
    .split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);

  const handleSaveProfile = async () => {
    setLoading(true);
    try {
      await updateProfile(auth.currentUser, { displayName: name });
      showToast(t.profileSaved);
    } catch { showToast('Error saving.', 'error'); }
    finally { setLoading(false); }
  };

  const handleChangePassword = async () => {
    if (newPass !== confirmPass) { showToast(t.passNoMatch, 'error'); return; }
    if (newPass.length < 6)     { showToast(t.passShort,   'error'); return; }
    setLoading(true);
    try {
      const cred = EmailAuthProvider.credential(user.email, currentPass);
      await reauthenticateWithCredential(auth.currentUser, cred);
      await updatePassword(auth.currentUser, newPass);
      setCurrentPass(''); setNewPass(''); setConfirmPass('');
      showToast(t.passSaved);
    } catch (e) {
      showToast(e.code === 'auth/wrong-password' ? t.reAuthError : 'Error.', 'error');
    } finally { setLoading(false); }
  };

  return (
    <div style={s.tabContent}>
      {toast.msg && <div style={{ ...s.toast, ...(toast.type === 'error' ? s.toastError : {}) }}>{toast.msg}</div>}

      {/* Avatar + name */}
      <div style={s.avatarRow}>
        <div style={s.avatarCircle}>{initials}</div>
        <div>
          <div style={s.avatarLabel}>{t.avatar}</div>
          <div style={s.avatarNote}>{t.avatarNote}</div>
        </div>
      </div>

      <div style={s.sectionTitle}>{t.profileSection}</div>
      <div style={s.grid2}>
        <div>
          <label style={s.label}>{t.nameLabel}</label>
          <input style={s.input} value={name} onChange={e => setName(e.target.value)} />
        </div>
        <div>
          <label style={s.label}>{t.emailLabel}</label>
          <input style={{ ...s.input, opacity: 0.45, cursor: 'not-allowed' }} value={user?.email || ''} readOnly />
          <p style={s.fieldNote}>{t.emailNote}</p>
        </div>
      </div>
      <button style={s.primaryBtn} onClick={handleSaveProfile} disabled={loading}>
        {loading ? '...' : t.saveProfile}
      </button>

      <div style={s.divider} />

      <div style={s.sectionTitle}>{t.passwordSection}</div>
      <div style={s.grid2}>
        <div>
          <label style={s.label}>{t.currentPass}</label>
          <input style={s.input} type="password" value={currentPass} onChange={e => setCurrentPass(e.target.value)} />
        </div>
        <div />
        <div>
          <label style={s.label}>{t.newPass}</label>
          <input style={s.input} type="password" value={newPass} onChange={e => setNewPass(e.target.value)} />
        </div>
        <div>
          <label style={s.label}>{t.confirmPass}</label>
          <input style={s.input} type="password" value={confirmPass} onChange={e => setConfirmPass(e.target.value)} />
        </div>
      </div>
      <button style={s.secondaryBtn} onClick={handleChangePassword} disabled={loading}>
        {loading ? '...' : t.savePassword}
      </button>
    </div>
  );
}

// ── Tab 2: Company & Logo ────────────────────────────────────────────────────
function CompanyTab({ lang }) {
  const fileRef = useRef();
  const [logoPreview, setLogoPreview] = useState(getUserLogo());
  const [companyName, setCompanyName] = useState(localStorage.getItem('coalab_company_name') || '');
  const [companyWeb,  setCompanyWeb]  = useState(localStorage.getItem('coalab_company_web')  || '');
  const [companyAddr, setCompanyAddr] = useState(localStorage.getItem('coalab_company_addr') || '');
  const [toast,       setToast]       = useState({ msg: '', type: '' });
  const [logoError,   setLogoError]   = useState('');

  const tx = {
    en: {
      logoSection: 'Company Logo',
      logoDesc: 'Appears in the header of every PDF certificate you generate.',
      logoHint: 'PNG or JPG · Max 2 MB · Horizontal format recommended (e.g. 400×120 px)',
      upload: '⬆ Upload Logo', change: '⬆ Change Logo', remove: 'Remove',
      noLogo: 'No logo uploaded — COA Lab default will be used',
      logoSaved: 'Logo saved!', logoRemoved: 'Logo removed.',
      companySection: 'Company Information',
      companyNote: 'This information can be pre-filled on generated certificates.',
      nameLabel: 'Company Name', webLabel: 'Website', addrLabel: 'Address',
      saveCompany: 'Save Company Info', companySaved: 'Company info saved!',
      preview: 'PDF Header Preview',
    },
    de: {
      logoSection: 'Unternehmenslogo',
      logoDesc: 'Erscheint in der Kopfzeile jedes PDF-Zertifikats.',
      logoHint: 'PNG oder JPG · Max. 2 MB · Horizontales Format empfohlen (z.B. 400×120 px)',
      upload: '⬆ Logo hochladen', change: '⬆ Logo ändern', remove: 'Entfernen',
      noLogo: 'Kein Logo — COA Lab Standard wird verwendet',
      logoSaved: 'Logo gespeichert!', logoRemoved: 'Logo entfernt.',
      companySection: 'Unternehmensdaten',
      companyNote: 'Diese Daten können auf generierten Zertifikaten vorausgefüllt werden.',
      nameLabel: 'Unternehmensname', webLabel: 'Website', addrLabel: 'Adresse',
      saveCompany: 'Unternehmensdaten speichern', companySaved: 'Daten gespeichert!',
      preview: 'PDF-Kopfzeilen-Vorschau',
    },
  };
  const t = tx[lang] || tx.en;

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast({ msg: '', type: '' }), 3000);
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setLogoError('');
    if (!['image/png', 'image/jpeg'].includes(file.type)) {
      setLogoError('Only PNG and JPG supported.'); return;
    }
    if (file.size > 2 * 1024 * 1024) {
      setLogoError('File too large. Max 2 MB.'); return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => {
      localStorage.setItem(LOGO_KEY, ev.target.result);
      setLogoPreview(ev.target.result);
      showToast(t.logoSaved);
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveLogo = () => {
    clearUserLogo();
    setLogoPreview(null);
    showToast(t.logoRemoved);
  };

  const handleSaveCompany = () => {
    localStorage.setItem('coalab_company_name', companyName);
    localStorage.setItem('coalab_company_web',  companyWeb);
    localStorage.setItem('coalab_company_addr', companyAddr);
    showToast(t.companySaved);
  };

  return (
    <div style={s.tabContent}>
      {toast.msg && <div style={{ ...s.toast, ...(toast.type === 'error' ? s.toastError : {}) }}>{toast.msg}</div>}

      {/* Logo section */}
      <div style={s.sectionTitle}>{t.logoSection}</div>
      <p style={s.sectionDesc}>{t.logoDesc}</p>
      <p style={s.fieldNote}>{t.logoHint}</p>

      {logoError && <div style={s.errorBox}>{logoError}</div>}

      <div style={s.logoUploadArea}>
        {logoPreview
          ? <img src={logoPreview} alt="Company logo" style={s.logoImg} />
          : <div style={s.logoEmpty}>
              <span style={{ fontSize: 32 }}>🏢</span>
              <span style={s.logoEmptyText}>{t.noLogo}</span>
            </div>
        }
      </div>
      <div style={s.logoActions}>
        <button style={s.uploadBtn} onClick={() => fileRef.current.click()}>
          {logoPreview ? t.change : t.upload}
        </button>
        {logoPreview && (
          <button style={s.removeBtn} onClick={handleRemoveLogo}>{t.remove}</button>
        )}
      </div>
      <input ref={fileRef} type="file" accept="image/png,image/jpeg" style={{ display: 'none' }} onChange={handleLogoUpload} />

      {/* PDF preview */}
      <div style={s.pdfPreviewWrap}>
        <div style={s.pdfPreviewLabel}>{t.preview}</div>
        <div style={s.pdfHeaderPreview}>
          <div style={s.pdfLeft}>
            {logoPreview
              ? <img src={logoPreview} alt="logo" style={{ maxHeight: 38, maxWidth: 140, objectFit: 'contain' }} />
              : <div style={s.pdfDefaultLogo}>
                  <span style={s.pdfLogoMain}>COA</span>
                  <span style={s.pdfLogoAccent}>Lab</span>
                </div>
            }
          </div>
          <div style={s.pdfRight}>
            <div style={s.pdfTitleText}>
              {lang === 'de' ? 'ANALYSENZERTIFIKAT' : 'CERTIFICATE OF ANALYSIS'}
            </div>
            <div style={s.pdfIdText}>ID: COA-2026-XXXX</div>
          </div>
        </div>
        <div style={s.pdfAccentLine} />
      </div>

      <div style={s.divider} />

      {/* Company info */}
      <div style={s.sectionTitle}>{t.companySection}</div>
      <p style={s.sectionDesc}>{t.companyNote}</p>
      <div style={s.grid2}>
        <div>
          <label style={s.label}>{t.nameLabel}</label>
          <input style={s.input} value={companyName} onChange={e => setCompanyName(e.target.value)} />
        </div>
        <div>
          <label style={s.label}>{t.webLabel}</label>
          <input style={s.input} value={companyWeb} onChange={e => setCompanyWeb(e.target.value)} placeholder="https://" />
        </div>
        <div style={{ gridColumn: '1/-1' }}>
          <label style={s.label}>{t.addrLabel}</label>
          <input style={s.input} value={companyAddr} onChange={e => setCompanyAddr(e.target.value)} />
        </div>
      </div>
      <button style={s.primaryBtn} onClick={handleSaveCompany}>{t.saveCompany}</button>
    </div>
  );
}

// ── Tab 3: Preferences ───────────────────────────────────────────────────────
function PreferencesTab({ lang, switchLang }) {
  const [defaultLang,    setDefaultLang]    = useState(localStorage.getItem('coalab_lang') || 'en');
  const [defaultMethod,  setDefaultMethod]  = useState(localStorage.getItem('coalab_def_method')  || 'HPLC');
  const [defaultCountry, setDefaultCountry] = useState(localStorage.getItem('coalab_def_country') || '');
  const [autoQR,         setAutoQR]         = useState(localStorage.getItem('coalab_auto_qr') !== 'false');
  const [autoLogo,       setAutoLogo]       = useState(localStorage.getItem('coalab_auto_logo') !== 'false');
  const [toast,          setToast]          = useState('');

  const tx = {
    en: {
      langSection: 'Language',
      langLabel: 'Interface & PDF language',
      langNote: 'This affects both the app interface and the language of generated PDFs.',
      defaultsSection: 'Certificate Defaults',
      defaultsNote: 'These values will be pre-filled when starting a new COA.',
      methodLabel: 'Default testing method',
      countryLabel: 'Default country of origin',
      optionsSection: 'PDF Options',
      autoQR: 'Include QR code by default',
      autoLogo: 'Include logo by default',
      save: 'Save Preferences', saved: 'Preferences saved!',
      en: 'English', de: 'Deutsch',
    },
    de: {
      langSection: 'Sprache',
      langLabel: 'Oberflächen- & PDF-Sprache',
      langNote: 'Betrifft sowohl die App-Oberfläche als auch die Sprache der generierten PDFs.',
      defaultsSection: 'Zertifikat-Standardwerte',
      defaultsNote: 'Diese Werte werden beim Erstellen eines neuen ZA vorausgefüllt.',
      methodLabel: 'Standard-Testmethode',
      countryLabel: 'Standard-Herkunftsland',
      optionsSection: 'PDF-Optionen',
      autoQR: 'QR-Code standardmäßig einschließen',
      autoLogo: 'Logo standardmäßig einschließen',
      save: 'Einstellungen speichern', saved: 'Einstellungen gespeichert!',
      en: 'English', de: 'Deutsch',
    },
  };
  const t = tx[lang] || tx.en;

  const handleSave = () => {
    localStorage.setItem('coalab_lang',        defaultLang);
    localStorage.setItem('coalab_def_method',  defaultMethod);
    localStorage.setItem('coalab_def_country', defaultCountry);
    localStorage.setItem('coalab_auto_qr',     autoQR);
    localStorage.setItem('coalab_auto_logo',   autoLogo);
    switchLang(defaultLang);
    setToast(t.saved);
    setTimeout(() => setToast(''), 3000);
  };

  return (
    <div style={s.tabContent}>
      {toast && <div style={s.toast}>{toast}</div>}

      {/* Language */}
      <div style={s.sectionTitle}>{t.langSection}</div>
      <label style={s.label}>{t.langLabel}</label>
      <div style={s.langOptions}>
        {['en', 'de'].map(l => (
          <button
            key={l}
            style={{ ...s.langOption, ...(defaultLang === l ? s.langOptionActive : {}) }}
            onClick={() => setDefaultLang(l)}
          >
            <span style={s.langFlag}>{l === 'en' ? '🇬🇧' : '🇩🇪'}</span>
            <span>{t[l]}</span>
          </button>
        ))}
      </div>
      <p style={s.fieldNote}>{t.langNote}</p>

      <div style={s.divider} />

      {/* Certificate defaults */}
      <div style={s.sectionTitle}>{t.defaultsSection}</div>
      <p style={s.sectionDesc}>{t.defaultsNote}</p>
      <div style={s.grid2}>
        <div>
          <label style={s.label}>{t.methodLabel}</label>
          <select style={s.select} value={defaultMethod} onChange={e => setDefaultMethod(e.target.value)}>
            {['HPLC', 'GC', 'USP', 'IR', 'Visual', 'ICH', 'AAS', 'NMR', 'GMP'].map(m => (
              <option key={m} value={m}>{m}</option>
            ))}
          </select>
        </div>
        <div>
          <label style={s.label}>{t.countryLabel}</label>
          <input style={s.input} value={defaultCountry} onChange={e => setDefaultCountry(e.target.value)} placeholder="e.g. Germany" />
        </div>
      </div>

      <div style={s.divider} />

      {/* PDF options */}
      <div style={s.sectionTitle}>{t.optionsSection}</div>
      <div style={s.toggleList}>
        <div style={s.toggleRow}>
          <span style={s.toggleLabel}>{t.autoQR}</span>
          <Toggle checked={autoQR} onChange={setAutoQR} />
        </div>
        <div style={s.toggleRow}>
          <span style={s.toggleLabel}>{t.autoLogo}</span>
          <Toggle checked={autoLogo} onChange={setAutoLogo} />
        </div>
      </div>

      <button style={s.primaryBtn} onClick={handleSave}>{t.save}</button>
    </div>
  );
}

function Toggle({ checked, onChange }) {
  return (
    <button
      onClick={() => onChange(!checked)}
      style={{
        width: 46, height: 26, borderRadius: 13, border: 'none', cursor: 'pointer',
        background: checked ? '#1E5EFF' : 'rgba(255,255,255,0.15)',
        position: 'relative', transition: 'background 0.2s', flexShrink: 0,
      }}
    >
      <span style={{
        position: 'absolute', top: 3,
        left: checked ? 23 : 3,
        width: 20, height: 20, borderRadius: 10,
        background: '#fff', transition: 'left 0.2s',
      }} />
    </button>
  );
}

const s = {
  page:      { minHeight: '100vh', background: '#070F2B', padding: '84px 24px 60px', maxWidth: 860, margin: '0 auto', fontFamily: "'Inter', sans-serif", color: '#fff' },
  pageTitle: { fontSize: 28, fontWeight: 800, color: '#fff', marginBottom: 24 },

  // Tabs
  tabBar:    { display: 'flex', gap: 4, marginBottom: 28, background: 'rgba(255,255,255,0.04)', borderRadius: 14, padding: 4, flexWrap: 'wrap' },
  tab:       { flex: 1, minWidth: 140, background: 'none', border: 'none', color: 'rgba(255,255,255,0.5)', fontSize: 14, fontWeight: 600, padding: '10px 16px', borderRadius: 10, cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.2s', whiteSpace: 'nowrap' },
  tabActive: { background: 'rgba(255,255,255,0.08)', color: '#fff' },
  panel:     { background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 18 },
  tabContent:{ padding: '32px 28px' },

  // Form
  sectionTitle: { fontSize: 16, fontWeight: 700, color: '#fff', marginBottom: 6, marginTop: 4 },
  sectionDesc:  { fontSize: 13.5, color: 'rgba(255,255,255,0.5)', marginBottom: 16, lineHeight: 1.6 },
  grid2:     { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px,1fr))', gap: 16, marginBottom: 20 },
  label:     { display: 'block', fontSize: 13, fontWeight: 600, color: 'rgba(255,255,255,0.65)', marginBottom: 6 },
  input:     { width: '100%', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 10, padding: '11px 14px', color: '#fff', fontSize: 14, outline: 'none', boxSizing: 'border-box' },
  select:    { width: '100%', background: '#0E1840', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 10, padding: '11px 14px', color: '#fff', fontSize: 14, outline: 'none', boxSizing: 'border-box', cursor: 'pointer', appearance: 'auto' },
  fieldNote: { fontSize: 12, color: 'rgba(255,255,255,0.3)', marginTop: 5 },
  divider:   { height: 1, background: 'rgba(255,255,255,0.07)', margin: '28px 0' },

  // Buttons
  primaryBtn:  { background: 'linear-gradient(135deg,#1E5EFF,#0A3DD4)', border: 'none', color: '#fff', padding: '12px 24px', borderRadius: 10, fontSize: 14, fontWeight: 700, cursor: 'pointer', marginTop: 4 },
  secondaryBtn:{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.15)', color: '#fff', padding: '12px 24px', borderRadius: 10, fontSize: 14, fontWeight: 600, cursor: 'pointer', marginTop: 4 },

  // Toast
  toast:     { background: 'rgba(52,211,153,0.12)', border: '1px solid rgba(52,211,153,0.3)', borderRadius: 10, padding: '12px 16px', color: '#6EE7B7', fontSize: 14, marginBottom: 20 },
  toastError:{ background: 'rgba(239,68,68,0.1)',   border: '1px solid rgba(239,68,68,0.3)',   color: '#FCA5A5' },
  errorBox:  { background: 'rgba(239,68,68,0.1)',   border: '1px solid rgba(239,68,68,0.3)',   borderRadius: 10, padding: '10px 14px', color: '#FCA5A5', fontSize: 13, marginBottom: 14 },

  // Avatar
  avatarRow:    { display: 'flex', alignItems: 'center', gap: 16, marginBottom: 28, padding: '16px', background: 'rgba(255,255,255,0.03)', borderRadius: 14 },
  avatarCircle: { width: 56, height: 56, borderRadius: 28, background: 'linear-gradient(135deg,#1E5EFF,#4A9EFF)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, fontWeight: 800, color: '#fff', flexShrink: 0 },
  avatarLabel:  { fontSize: 15, fontWeight: 700, color: '#fff', marginBottom: 3 },
  avatarNote:   { fontSize: 12, color: 'rgba(255,255,255,0.4)' },

  // Logo upload
  logoUploadArea: { background: 'rgba(255,255,255,0.03)', border: '2px dashed rgba(255,255,255,0.1)', borderRadius: 14, padding: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 110, marginBottom: 14 },
  logoImg:        { maxHeight: 80, maxWidth: 280, objectFit: 'contain' },
  logoEmpty:      { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 },
  logoEmptyText:  { fontSize: 13, color: 'rgba(255,255,255,0.3)', textAlign: 'center' },
  logoActions:    { display: 'flex', gap: 10, marginBottom: 24 },
  uploadBtn:      { background: 'rgba(74,158,255,0.1)', border: '1px solid rgba(74,158,255,0.3)', color: '#4A9EFF', padding: '10px 20px', borderRadius: 10, fontSize: 14, fontWeight: 600, cursor: 'pointer' },
  removeBtn:      { background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)', color: '#F87171', padding: '10px 18px', borderRadius: 10, fontSize: 14, cursor: 'pointer' },

  // PDF preview
  pdfPreviewWrap:   { marginBottom: 8 },
  pdfPreviewLabel:  { fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.35)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 10 },
  pdfHeaderPreview: { background: 'linear-gradient(135deg,#0A2878,#143090)', borderRadius: '10px 10px 0 0', padding: '16px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 },
  pdfLeft:          { flex: '0 0 auto' },
  pdfDefaultLogo:   { display: 'flex', alignItems: 'baseline', gap: 3 },
  pdfLogoMain:      { fontSize: 18, fontWeight: 900, color: '#fff' },
  pdfLogoAccent:    { fontSize: 10, fontWeight: 700, color: '#82B4FF' },
  pdfRight:         { textAlign: 'right' },
  pdfTitleText:     { fontSize: 11, fontWeight: 800, color: '#fff', letterSpacing: 1 },
  pdfIdText:        { fontSize: 9, color: '#82B4FF', marginTop: 4, fontFamily: 'monospace' },
  pdfAccentLine:    { height: 3, background: 'linear-gradient(to right,#4A9EFF,#1E5EFF)', borderRadius: '0 0 6px 6px', marginBottom: 20 },

  // Lang options
  langOptions:     { display: 'flex', gap: 12, marginBottom: 8, marginTop: 8 },
  langOption:      { display: 'flex', alignItems: 'center', gap: 10, background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 12, padding: '12px 20px', cursor: 'pointer', fontSize: 14, fontWeight: 600, color: 'rgba(255,255,255,0.6)', fontFamily: 'inherit', transition: 'all 0.2s' },
  langOptionActive:{ background: 'rgba(30,94,255,0.15)', border: '1px solid rgba(30,94,255,0.4)', color: '#fff' },
  langFlag:        { fontSize: 20 },

  // Toggles
  toggleList: { display: 'flex', flexDirection: 'column', gap: 0, marginBottom: 24 },
  toggleRow:  { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 0', borderBottom: '1px solid rgba(255,255,255,0.06)' },
  toggleLabel:{ fontSize: 14, color: 'rgba(255,255,255,0.75)' },
};
